import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import get from 'lodash/get';
import SubGroupMultipleDatabases from '@Containers/ResultsPreview/GroupMultipleDatabases/SubGroupMultipleDatabases';
import BlockContextColor from '@Containers/ResultsPreview/Components/BlockContextColor';
import BlockLabel from '@Containers/ResultsPreview/Components/BlockLabel';
import BlockFeedbacks from '@Containers/ResultsPreview/Components/BlockFeedbacks';
import BlockActionButtons from '@Containers/ResultsPreview/Components/BlockActionButtons';

import './GroupMultipleDatabases.theme.scss';

function GroupMultipleDatabases({
  previewItem,
  showMoreResults,
  showStructurePopup,
  copyQuery,
  createAlert,
  redirectToResults,
  cancelQuery,
  cancelQueriesInGroup,
  expandedByDefault,
}) {
  const showProgressLoader = !previewItem.fullyLoaded && !get(previewItem, 'query.cancelled');
  const showQueryCancelled = !previewItem.fullyLoaded && get(previewItem, 'query.cancelled');
  const showMultipleDatabasesExpand = previewItem.groupHasResults;
  const showPredictionButton = previewItem.fullyLoaded && !get(previewItem, 'query.cancelled');
  const iconName = get(previewItem, 'title', '').toLowerCase().replace(' ', '-');

  const [expanded, setExpanded] = useState(false);
  const toggleExpanded = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);

  useEffect(() => {
    if (expandedByDefault && previewItem.groupHasResults) {
      setExpanded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewItem.groupHasResults]);

  return (
    <div
      className={cn('quick-results-preview__item e2e-quick-results-item group-multiple-databases', {
        'quick-results-preview__item-loading': !previewItem.fullyLoaded,
      })}>
      <BlockContextColor previewItem={previewItem} showIcon />

      <div className="quick-results-preview__item_wrapper">
        <div className="quick-results-preview__item_controls">
          <BlockLabel previewItem={previewItem} showResultsCount={previewItem.fullyLoaded && !previewItem.groupHasResults} showTitle />
          <BlockActionButtons
            previewItem={previewItem}
            showProgressLoader={showProgressLoader}
            onCancelClick={() => cancelQueriesInGroup(previewItem.groupId)}
            showQueryCancelled={showQueryCancelled}
            showExpandButton={showMultipleDatabasesExpand}
            expandButtonConfig={{
              label: 'Multiple databases',
              e2eName: 'multiple-databases',
            }}
            expandButtonAttrs={{
              'aria-label': 'Multiple results from different databases',
            }}
            expanded={expanded}
            toggleExpanded={toggleExpanded}
            showViewResultsButton={false}
            showPredictionButton={showPredictionButton}
            showBioactivityVisualizationButton={false}
          />
          <div className="quick-results-preview__item_controls_wrapper">
            <BlockFeedbacks
              previewItem={previewItem}
              showStructurePopup={showStructurePopup}
              copyQuery={copyQuery}
              createAlert={createAlert}
              showFeedbacks
            />
          </div>
        </div>

        {expanded && (
          <ul className={`quick-results-preview__item_preview_ quick-results-preview__item_preview-${iconName}`}>
            {previewItem.multipleDatabasesItems.map((subItem, subItemIndex) => (
              <SubGroupMultipleDatabases
                key={subItem.queryId}
                previewItem={subItem}
                previewItemIndex={subItemIndex}
                showMoreResults={showMoreResults}
                redirectToResults={redirectToResults}
                cancelQuery={cancelQuery}
                isLast={subItemIndex === previewItem.multipleDatabasesItems.length - 1}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

GroupMultipleDatabases.propTypes = {
  previewItem: PropTypes.object.isRequired,
  showMoreResults: PropTypes.func.isRequired,
  showStructurePopup: PropTypes.func.isRequired,
  copyQuery: PropTypes.func.isRequired,
  createAlert: PropTypes.func.isRequired,
  redirectToResults: PropTypes.func.isRequired,
  cancelQuery: PropTypes.func.isRequired,
  cancelQueriesInGroup: PropTypes.func.isRequired,
  expandedByDefault: PropTypes.bool,
};

export default GroupMultipleDatabases;
